import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  HeartIcon as HeartSolid,
  XMarkIcon,
  MapPinIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/solid';
import { HeartIcon as HeartOutline, ArrowPathIcon, ShareIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { getCategoryColor } from '../../utils/categoryColors';
import { formatEventTime } from '../../utils/formatEventDate';
import { getEventPlaceholder } from '../../utils/eventPlaceholder';

const EventModal = ({
  event,
  formatEventDate,
  normalizeKey,
  savedEventIds,
  isDescExpanded,
  onToggleDesc,
  onClose,
  onToggleSave,
  t,
  i18nLanguage,
}) => {
  const closeBtnRef = useRef(null);
  const descRef = useRef(null);
  const imgContainerRef = useRef(null);
  const [isDescClamped, setIsDescClamped] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);

  useEffect(() => {
    const el = descRef.current;
    if (el && !isDescExpanded) setIsDescClamped(el.scrollHeight > el.clientHeight + 1);
  }, [event, isDescExpanded]);

  useEffect(() => {
    if (!event) return;
    const container = imgContainerRef.current;
    if (!container) return;
    const check = () => setShowScrollHint(container.scrollHeight > container.clientHeight + 1 && container.scrollTop < 10);
    const img = container.querySelector('img');
    if (img) img.addEventListener('load', check);
    check();
    container.addEventListener('scroll', () => setShowScrollHint(container.scrollTop < 10 && container.scrollHeight > container.clientHeight + 1));
    return () => { if (img) img.removeEventListener('load', check); };
  }, [event]);

  // Lock body scroll (iOS-correct: position fixed + scroll restoration)
  useEffect(() => {
    if (!event) return;
    const scrollY = window.scrollY;
    const prev = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
      paddingRight: document.body.style.paddingRight,
    };
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.position = prev.position;
      document.body.style.top = prev.top;
      document.body.style.width = prev.width;
      document.body.style.overflow = prev.overflow;
      document.body.style.paddingRight = prev.paddingRight;
      window.scrollTo(0, scrollY);
    };
  }, [event]);

  // ESC to close
  useEffect(() => {
    if (!event) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [event, onClose]);

  // Initial focus on close button
  useEffect(() => {
    if (event) closeBtnRef.current?.focus();
  }, [event]);

  const handleShare = async () => {
    const url = `${window.location.origin}/events?event=${event._id}`;
    const text = `${event.title} — ${formatEventDate(event.start, event.end)}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: event.title, text, url });
      } catch (err) {
        if (err.name !== 'AbortError') {
          await navigator.clipboard.writeText(url);
          toast.success(t('events.link-copied'));
        }
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success(t('events.link-copied'));
    }
  };

  if (!event) return null;
  const { primary, light } = getCategoryColor(event.category);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center p-0 md:p-4 bg-black/80 backdrop-blur-md"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-modal-title"
        className="relative bg-white rounded-t-[2rem] md:rounded-2xl w-full max-w-2xl max-h-[90dvh] overflow-hidden shadow-2xl safe-area-pb"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-center items-center min-h-[24px] md:hidden">
          <div className="w-12 h-1 bg-gray-300 rounded-full" />
        </div>

        <div className="hidden md:block h-1.5 w-full" style={{ backgroundColor: primary }} />

        <div className="max-h-[90dvh] overflow-y-auto custom-scrollbar pb-10 overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>

          <div ref={imgContainerRef} className="relative w-full h-48 sm:h-64 md:h-96 overflow-y-auto bg-gray-100 border-b border-gray-100 custom-scrollbar">
            <img
              src={event.photoUrl || getEventPlaceholder(event.category)}
              className="w-full h-auto min-h-full object-cover"
              alt={event.title}
            />

            <button
              ref={closeBtnRef}
              onClick={onClose}
              aria-label={t('events.modal.close') || 'Close'}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d5cab] transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>

            {showScrollHint && (
              <div className="sticky bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-black/40 to-transparent flex items-end justify-center pb-1 pointer-events-none transition-opacity duration-300">
                <ChevronDownIcon className="h-5 w-5 text-white animate-bounce" aria-hidden="true" />
              </div>
            )}
          </div>

          <div className="p-4 sm:p-6 md:p-8">
            <div className="flex justify-between items-start mb-4">
              <span
                className="px-3 py-1 rounded-full text-[10px] md:text-xs font-black uppercase tracking-wider shadow-sm"
                style={{ backgroundColor: light, color: primary }}
              >
                {t(`events.event-management.event-type.${normalizeKey(event.category)}`, { defaultValue: event.category })}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleShare}
                  aria-label={t('events.share-event')}
                  className="p-2 -mt-1 rounded-full hover:bg-gray-100 transition-transform active:scale-125"
                >
                  <ShareIcon className="h-7 w-7 text-gray-400" />
                </button>
                <button
                  onClick={(e) => onToggleSave(e, event._id)}
                  aria-label={savedEventIds.includes(event._id) ? t('events.unsave-event') : t('events.save-event')}
                  className="p-2 -mt-1 rounded-full hover:bg-gray-100 transition-transform active:scale-125"
                >
                  {savedEventIds.includes(event._id) ?
                    <HeartSolid className="h-8 w-8 text-red-500" /> :
                    <HeartOutline className="h-8 w-8 text-gray-400" />
                  }
                </button>
              </div>
            </div>

            <h2 id="event-modal-title" className="text-2xl md:text-4xl font-black text-gray-900 mb-3 leading-tight">
              {event.title}
            </h2>

            <div className="flex flex-col gap-1 mb-6">
              <p className="text-[#f05537] font-extrabold text-lg md:text-xl">
                {formatEventDate(event.start, event.end)}
              </p>
              <p className="text-gray-500 text-sm font-medium">
                {formatEventTime(event.start, i18nLanguage)} – {formatEventTime(event.end, i18nLanguage)}
              </p>
            </div>

            <div className="relative border-t border-gray-100 pt-6">
              <div ref={descRef} className={`prose prose-sm text-gray-600 transition-all duration-500 ${!isDescExpanded ? 'max-h-32' : 'max-h-[2000px]'} overflow-hidden`}>
                <p className="whitespace-pre-line text-[15px] md:text-base leading-relaxed">
                  {event.description || t('events.no-description')}
                </p>
              </div>
              {(isDescClamped || isDescExpanded) && (
                <button
                  onClick={onToggleDesc}
                  className="mt-4 flex items-center gap-1 text-blue-600 font-bold text-sm hover:text-blue-800 min-h-[44px]"
                >
                  {isDescExpanded ? t('hero.read-less') : t('hero.read')}
                  {isDescExpanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                </button>
              )}
            </div>

            <div className="mt-8 p-4 bg-gray-50 rounded-2xl flex items-center gap-4 border border-gray-100">
              <div className="h-12 w-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                <MapPinIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">
                  {t('events.hosted-by')}
                </p>
                <p className="text-gray-900 font-black text-base md:text-lg truncate">
                  {event.provider?.businessName}
                </p>
              </div>
            </div>

            {event.location?.useProvider === false &&
              (event.location.name || event.location.address) && (
              <div className="mt-3 p-4 bg-gray-50 rounded-2xl flex items-center gap-4 border border-gray-100">
                <div className="h-12 w-12 bg-white rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                  <MapPinIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">
                    {t('events.event-management.location.title')}
                  </p>
                  {event.location.name && (
                    <p className="text-gray-900 font-bold text-sm">{event.location.name}</p>
                  )}
                  {event.location.address && (
                    <p className="text-gray-500 text-xs">{event.location.address}</p>
                  )}
                  {event.location.lat && event.location.lng && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${event.location.address ? encodeURIComponent(event.location.address) : `${event.location.lat},${event.location.lng}`}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-blue-500 hover:text-blue-700"
                      onClick={e => e.stopPropagation()}
                    >
                      <MapPinIcon className="h-3 w-3" />
                      {t('events.viewOnMap')}
                    </a>
                  )}
                </div>
              </div>
            )}

            {event.recurrence?.type && event.recurrence.type !== 'none' && (
              <div className="mt-3 px-4 py-2.5 bg-slate-50 rounded-xl border border-slate-100 text-sm text-slate-500 font-medium inline-flex items-center gap-1">
                <ArrowPathIcon className="h-4 w-4 flex-shrink-0" />
                {t(`events.event-management.recurrence.${event.recurrence.type}`)}
                {event.recurrence.endDate && (
                  <span className="ml-2 text-xs text-slate-400">
                    · {t('events.event-management.recurrence.end-date')} {new Date(event.recurrence.endDate).toLocaleDateString(i18nLanguage)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EventModal;
